import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import * as React from "react";

// import { Box, Button, Grid, TextField } from "@mui/material";

import { CAN_USE_DOM } from "../../../utils/canUseDom";
import {
  $createImageNode,
  $isImageNode,
  ImageNode,
} from "../../../components/lexical/ImageNode";
import TextField from "../../../components/common/TextField";
import notify from "../../../components/common/notify";
import useApi from "../../../hooks/useApi";
import { ProgressBar } from "react-loader-spinner";

const getDOMSelection = (targetWindow) =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_IMAGE_COMMAND = createCommand("INSERT_IMAGE_COMMAND");

export function InsertImageUriDialogBody({ onClick }) {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");

  const isDisabled = src === "";

  return (
    <>
      <TextField
        containerClass="w-full"
        inputClass="h-10"
        placeholder="i.e. https://source.unsplash.com/random"
        onChange={(e) => setSrc(e.target.value)}
        value={src}
      />
      <TextField
        containerClass="w-full"
        inputClass="h-10"
        placeholder="Random unsplash image"
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        id={"image-modal-alt-text-input"}
      />
      <div className="w-full flex justify-end">
        <button
          className="py-2 px-4 text-white bg-primary rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
          id="image-modal-confirm-btn"
          disabled={isDisabled}
          type="button"
          onClick={() => onClick({ altText, src })}
        >
          Confirm
        </button>
      </div>
    </>
  );
}

export function InsertImageUploadedDialogBody({ onClick }) {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");
  const [fileName, setFileName] = useState(null);
  const { loading: uploadFileLoading, PUBLIC_REQUEST } = useApi();

  const isDisabled = src === "";

  const handleAttachFileChange = async (e) => {
    let formData = new FormData();
    formData.append("file", e.target.files[0]);
    setFileName(e.target.files[0].name);
    // Define success and error callbacks for file upload
    const onSuccess = (data) => {
      notify(data?.message, "success");
      // formik.setFieldValue("attachment", data?.link);
      setSrc(data?.link);
    };
    const onError = (error) => {
      if (error.status === 413) {
        notify(
          "The size of the uploaded file is large, please try again.",
          "error"
        );
      }
    };

    // Make a public request to upload the file
    await PUBLIC_REQUEST({
      url: "/upload/",
      method: "post",
      data: formData,
      notifyError: true,
      onSuccess,
      onError,
    });
  };
  return (
    <div className="flex flex-col gap-3">
      <label
        htmlFor="upload_image"
        className={`w-full h-10 flex justify-center items-center rounded-lg bg-primary-green text-white hover:bg-[#347f8e] ${
          uploadFileLoading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        {uploadFileLoading ? (
          <ProgressBar
            height="50"
            width="50"
            ariaLabel="progress-bar-loading"
            wrapperStyle={{}}
            wrapperClass="progress-bar-wrapper"
            borderColor="#FFFFFF"
            barColor="#FFFFFF"
          />
        ) : (
          "Upload"
        )}
        <input
          id="upload_image"
          // onChange={(e) => loadImage(e.target.files)}
          onChange={handleAttachFileChange}
          hidden
          accept="image/*"
          multiple
          type="file"
          disabled={uploadFileLoading}
        />
      </label>
      <p className="text-lg truncate">{fileName}</p>
      <TextField
        // title="Alt"
        containerClass="w-full"
        inputClass="h-10"
        id={"image-modal-alt-text-input"}
        placeholder="Descriptive alternative text"
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
      />
      <div className="w-full flex justify-end">
        <button
          className="py-2 px-4 text-white bg-primary rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
          id="image-modal-confirm-btn"
          disabled={isDisabled || uploadFileLoading}
          type="button"
          onClick={() => onClick({ altText, src })}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

export function InsertImageDialog({ activeEditor, onClose }) {
  const [mode, setMode] = useState(null);
  const hasModifier = useRef(false);

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [activeEditor]);

  const onClick = (payload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  return (
    <div className="flex flex-col gap-3">
      {!mode && (
        <div className="w-full flex justify-between gap-3">
          <button
            className="p-2 bg-slate-400 text-whitesmoke w-1/2 rounded-md"
            id="image-modal-option-url"
            onClick={() => setMode("url")}
          >
            URL
          </button>
          <button
            className="p-2 bg-slate-400 text-whitesmoke w-1/2 rounded-md"
            id="image-modal-option-file"
            onClick={() => setMode("file")}
          >
            File
          </button>
        </div>
      )}
      {mode === "url" && <InsertImageUriDialogBody onClick={onClick} />}
      {mode === "file" && <InsertImageUploadedDialogBody onClick={onClick} />}
    </div>
  );
}

export default function ImagesPlugin({ captionsEnabled }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNode(ImageNode)) {
      throw new Error("ImagesPlugin: ImageNode not registered on editor");
    }
    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          return onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          return onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          return onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [captionsEnabled, editor]);

  return null;
}

const TRANSPARENT_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

function onDragStart(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData("text/plain", "_");
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: "image",
    })
  );

  return true;
}

function onDragover(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function onDrop(event, editor) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function getImageNodeInSelection() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event) {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== "image") {
    return null;
  }

  return data;
}

function canDropImage(event) {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest("code, span.editor-image") &&
    target.parentElement &&
    target.parentElement.closest("div.ContentEditable__root")
  );
}

function getDragSelection(event) {
  let range;
  const target = event.target;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
      ? target.defaultView
      : target.ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
